import { formatDistanceToNow } from 'date-fns';

import type { Payment } from '@models/payments';
import { PaymentProviderStatuses, UiPaymentStatuses } from '@models/payments';
import {
  formatDeclineLine,
  formatPaymentAmount,
  formatPaymentDate,
  getDeclineInfo,
  getUiPaymentStatus,
} from '@features/payments/helpers';

import s from './PaymentTimeline.module.css';

type EventKind = 'success' | 'failure' | 'neutral';

type TimelineEvent = {
  id: string;
  name: string;
  timestamp: string;
  kind: EventKind;
  detail?: string;
};

const MIN_UPDATED_GAP_MS = 60_000;

function buildProviderStatusEvent(p: Payment): TimelineEvent | null {
  if (!p.completedAt || !p.providerStatus) return null;
  const status = p.providerStatus.toUpperCase();
  switch (status) {
    case PaymentProviderStatuses.AUTHORIZED:
      return {
        id: 'provider-status',
        name: 'Authorized',
        timestamp: p.completedAt,
        kind: 'neutral',
        detail: p.authorizationCode ? `Auth code ${p.authorizationCode}` : undefined,
      };
    case PaymentProviderStatuses.SETTLED:
      return {
        id: 'provider-status',
        name: 'Settled',
        timestamp: p.completedAt,
        kind: 'success',
      };
    case PaymentProviderStatuses.COMPLETED:
      return {
        id: 'provider-status',
        name: 'Completed',
        timestamp: p.completedAt,
        kind: 'success',
      };
    case PaymentProviderStatuses.PENDING:
      return {
        id: 'provider-status',
        name: 'Pending',
        timestamp: p.completedAt,
        kind: 'neutral',
      };
    case PaymentProviderStatuses.CANCELLED:
      return {
        id: 'provider-status',
        name: 'Cancelled',
        timestamp: p.completedAt,
        kind: 'neutral',
      };
    case PaymentProviderStatuses.DECLINED:
    case PaymentProviderStatuses.FAILED: {
      const decline = getDeclineInfo(p);
      return {
        id: 'provider-status',
        name: status === PaymentProviderStatuses.FAILED ? 'Failed' : 'Declined',
        timestamp: p.completedAt,
        kind: 'failure',
        detail: decline ? formatDeclineLine(decline) : undefined,
      };
    }
    default:
      return null;
  }
}

function buildEvents(p: Payment): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  events.push({ id: 'created', name: 'Created', timestamp: p.createdAt, kind: 'neutral' });

  const threeDS = p.customerPaymentMethod?.details?.threeDSecureAuthentication?.responseCode;
  if (threeDS && threeDS !== 'NOT_PERFORMED') {
    const code = threeDS.toUpperCase();
    const kind: EventKind = code.includes('FAIL')
      ? 'failure'
      : code.includes('SUCCESS') || code === 'AUTHENTICATED'
        ? 'success'
        : 'neutral';
    events.push({
      id: '3ds',
      name: '3DS challenge',
      timestamp: p.createdAt,
      kind,
      detail: threeDS,
    });
  }

  const providerEvent = buildProviderStatusEvent(p);
  if (providerEvent) events.push(providerEvent);

  if (p.capturedAt) {
    events.push({
      id: 'captured',
      name: 'Captured',
      timestamp: p.capturedAt,
      kind: 'success',
    });
  } else if (p.lastCaptureAttemptAt) {
    // A capture attempt with no captured_at is not necessarily a failure: Primer and Yuno accept the
    // capture and settle asynchronously, so captured_at only lands with the settle webhook. The payment
    // row records when we last tried, never what came back — so this stays neutral either way.
    events.push({
      id: 'capture-requested',
      name: 'Capture requested',
      timestamp: p.lastCaptureAttemptAt,
      kind: 'neutral',
      detail: 'awaiting settlement or retry',
    });
  }

  if (p.amountRefunded > 0) {
    const refundAmount = formatPaymentAmount(
      p.amountRefunded,
      p.currency,
      p.currency.decimalDigits,
    );
    events.push({
      id: 'refunded',
      name: p.amountRefunded < p.amount ? 'Partially refunded' : 'Refunded',
      timestamp: p.updatedAt,
      kind: 'neutral',
      detail: `${refundAmount} ${p.currency.code}`,
    });
  }

  if (!providerEvent && getUiPaymentStatus(p) === UiPaymentStatuses.FAILED && p.completedAt) {
    const decline = getDeclineInfo(p);
    events.push({
      id: 'declined',
      name: 'Declined',
      timestamp: p.completedAt,
      kind: 'failure',
      detail: decline ? formatDeclineLine(decline) : undefined,
    });
  }

  if (p.updatedAt) {
    const lastEventTs = events.reduce(
      (acc, e) => Math.max(acc, new Date(e.timestamp).getTime()),
      0,
    );
    if (new Date(p.updatedAt).getTime() > lastEventTs + MIN_UPDATED_GAP_MS) {
      events.push({ id: 'updated', name: 'Updated', timestamp: p.updatedAt, kind: 'neutral' });
    }
  }

  events.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  return events;
}

function dotClass(kind: EventKind): string {
  if (kind === 'success') return `${s.dot} ${s.dotSuccess}`;
  if (kind === 'failure') return `${s.dot} ${s.dotFailure}`;
  return s.dot;
}

type PaymentTimelineProps = {
  payment: Payment;
};

function PaymentTimeline({ payment }: PaymentTimelineProps) {
  const events = buildEvents(payment);

  return (
    <section className={s.section}>
      <div className={s.eyebrow}>Timeline</div>
      {events.length === 0 ? (
        <div className={s.empty}>No timeline events available.</div>
      ) : (
        <ul className={s.timeline}>
          {events.map((e) => {
            const date = new Date(e.timestamp);
            const fd = formatPaymentDate(date);
            const relative = formatDistanceToNow(date, { addSuffix: true });
            return (
              <li key={e.id} className={s.item}>
                <span className={dotClass(e.kind)} aria-hidden />
                <div className={s.body}>
                  <div className={s.headerRow}>
                    <span className={s.name}>{e.name}</span>
                    <span className={s.relative}>{relative}</span>
                  </div>
                  <div className={s.time} title={`${fd.date} ${fd.time}`}>
                    {fd.date} · {fd.time}
                  </div>
                  {e.detail && <div className={s.detail}>{e.detail}</div>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default PaymentTimeline;
